import * as ioPath from '../../../Lib/Object/Path/Immutable';
import * as moPath from '../../../Lib/Object/Path/Mutable';
import deepEquals from '../../../Lib/Object/deepEquals';
import isPojo from '../../../Lib/Object/isPojo';
import copy from 'fast-clone';
import { sortDesc } from '../../../Lib/Array/sort';

/*
 * changeTracking: These methods are used to calculate and apply state changes between objects.
 */

const orderKeysByHighestArrayIndices = (keys: any) =>
  keys.sort(
    sortDesc((key: any) =>
      // This converts our keys to arrays of comparable numbers eg 'x[0].z[1]' becomes [0, 1]
      // which can be used for ordering, (we often need to delete x[0] AFTER x[1])
      key
        .replace(/\]/gim, '')
        .split(/[.[]/gim)
        .map((z: any) => parseInt(z, 10))
        .filter((z: any) => !isNaN(z))
    )
  );

// Not a great type, would be better if it included the values from both sides like QESTNet does.
// Instead it is just an object where each key is the path and the value is a '1' to indicate it has changed.
type ChangeTrackerChanges = { [path: string]: 1 | undefined };

// see calculateChangeTracking for explanation
const internalCalculateChangeTracking = (a: any, b: any, opts: any = {}): ChangeTrackerChanges => {
  const { path = '' } = opts;
  if (isPojo(a) && isPojo(b)) {
    // Get all the keys to traverse from both objects, check for diffs, and collect the results into 1 object
    return Array.from(new Set(Object.keys(a).concat(Object.keys(b))))
      .map((key) => internalCalculateChangeTracking(a[key], b[key], { ...opts, path: path ? `${path}.${key}` : key }))
      .reduce((prev, cur) => ({ ...prev, ...cur }), {});
  } else if (Array.isArray(a) && Array.isArray(b)) {
    let obj = {};
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      obj = { ...obj, ...internalCalculateChangeTracking(a[i], b[i], { ...opts, path: `${path}[${i}]` }) };
    }

    return obj;
  }

  if ((a === undefined || a === null) && (b === undefined || b === null)) return {};

  return !deepEquals(a, b) ? { [path]: 1 } : {};
};

/**
 * Create an object that represents the difference between 2 objects in the format { 'path-to-difference': 1, ...more }
 * where the "1" represents true (i.e. it changed), not the value. We also don't care how it changed, e.g. for array
 * it could be an add or delete, as long as it's different it's a "1".
 *
 * eg:    a={x:{xx:0}, y:[{}, {}], z:[{}, {}]}
 *        b={x:{xx:1}, y:[{} ], z:[{}, {}, {}]}
 *        would result in change tracking of: { "x.xx": 1, "y[1]": 1, "z[2]": 1 }
 *
 * @param {*} a - first object for comparison
 * @param {*} b - second object for comparison
 */
export const calculateChangeTracking = (a: any, b: any) => {
  const changeTracking = internalCalculateChangeTracking(a, b);
  return { changeTracking, changeCount: Object.keys(changeTracking).length };
};

/**
 * Examines the client state before and after the new server state arrives, and strategically applies
 * changes to the client state carefully (as to not overwrite any new client changes that have happened since).
 *
 * @param {object} clientModelBefore - The client state prior to the server request
 * @param {object} clientModelAfter - The client state after the server request was made (current client state)
 * @param {object} serverModelReceived - The new server state retrieved from the request
 * @param {object} serverModelSent - The model sent to the server to request updates.
 */
export const applyChangeTrackingPatch = ({
  clientModelAfter,
  clientModelBefore,
  serverModelSent,
  serverModelReceived,
}: any) => {
  // Always at the very least update the server to be at the state sent to ensure no infinite loops
  const server = copy(serverModelSent);
  // Update the client (and server) of any changes retrieved from the server that do not conflict with other client-made changes.
  const client = copy(clientModelAfter);

  // Build a comprehensive (specific) list of paths that have been modified by the client _while the update has occcured_
  const clientChanges = Object.entries(internalCalculateChangeTracking(clientModelAfter, clientModelBefore))
    .map(([key, val]) => (Array.isArray(val) ? val.map((v) => `${key}[${v.slice(1)}]`) : [key]))
    .reduce((prev, cur) => prev.concat(cur), []);

  // Build a list of paths the server has changed (that were not requested to be changed)
  const serverReceivedChanges = Object.keys(internalCalculateChangeTracking(serverModelSent, serverModelReceived));

  // Ignore server changes if they have also changed on the client (let the _next_ applyChangeTrackingPatch handle them)
  // as there may be client changes that occured that should be applied first (client changes are source of truth).
  const serverForcedChanges = serverReceivedChanges.filter((key) =>
    clientChanges.every(
      (cliChanged) =>
        // Check for both server modifying child object of client change AND server modifying parent object of client change
        key.indexOf(cliChanged) !== 0 && cliChanged.indexOf(key)
    )
  );

  // Need to be careful here and apply diff in appropriate order index:3 should be removed before index:1 or index:3 will not
  // exist in [a, b, c]
  const orderedServerForcedChanges = orderKeysByHighestArrayIndices(serverForcedChanges);

  // Now apply forced value changes from server response to our current server state and the client state.
  orderedServerForcedChanges.forEach((key: string) => {
    const sVal = ioPath.get(serverModelReceived, key);
    if (typeof sVal === 'undefined') {
      moPath.del(client, key);
      moPath.del(server, key);
    } else {
      moPath.set(client, key, sVal, true);
      moPath.set(server, key, sVal, true);
    }
  });

  return { client, server };
};
